// src/calculation/generateTables/processGenerate.js

import {
  processSectorFillOnTheFly,
  createSector
} from "./processSectorFillOnTheFly";
import { generateLayouts } from "./generate";
import {
  parseSplitDimension,
  tableUtilization,
  normalizeDirection,
  sameColorAndMilling,
  rectKey,
  restoreOriginalRectForRetry,
  getResultArea,
  isBetterPlan,
  logOptimizePlanResult
} from "./generateUtils";
import {
  resolveCutLabel,
  buildRighe,
  buildRigheAbs,
  mergeCutSequences,
  buildDati,
  buildRiferimenti,
  computeKantTotals,
  computeCutLength,
  CutDirection,
  KERF
} from "./cutUtils";

const SAME_MM_TOL = 1.0;
const LAYOUT_CANDIDATE_COUNT = 25;
const SCORE_EPS = 1e-9;
const OptimizeSubMode = {
  NOSPLIT: "nosplit",
  POSTSPLIT: "postsplit",
  PRESPLIT: "presplit"
};

function addCutsToLayout({
  layout,
  rootDir,
  sheetW,
  sheetH,
  tableIndex,
  id,
  TRIM
}) {
  // Az optimalizált layout méretei tartalmazzák a fűrészrést. A replay
  // bemenet viszont a darabok nettó, eredeti tájolású méreteit várja.
  const layoutForCutGeneration = layout.placedRects.map((rect) => {
    const placedW = rect.width - KERF;
    const placedH = rect.height - KERF;

    return {
      ...rect,
      width: rect.rotated ? placedH : placedW,
      height: rect.rotated ? placedW : placedH
    };
  });
  const cutPlacedRects = [];
  const cuts = [];

  processSectorFillOnTheFly(
    createSector({
      tableIndex,
      x: TRIM,
      y: TRIM,
      w: sheetW - TRIM,
      h: sheetH - TRIM,
      dir: rootDir,
      level: 0,
      rootMainDir: rootDir,
      isFullHeight: true
    }),
    layoutForCutGeneration,
    cutPlacedRects,
    tableIndex,
    id,
    cuts,
    "layout",
    TRIM
  );

  const cutDataByRect = new Map(
    cutPlacedRects.map((rect) => [rect.localid, rect])
  );
  const placedRects = layout.placedRects.map((rect) => {
    const cutRect = cutDataByRect.get(rect.localid);
    return cutRect
      ? {
          ...rect,
          releaseCutId: cutRect.releaseCutId,
          isLastInSector: cutRect.isLastInSector
        }
      : rect;
  });

  return {
    ...layout,
    placedRects,
    cuts,
    _cutLayoutComplete:
      layoutForCutGeneration.length === 0 &&
      cutPlacedRects.length === layout.placedRects.length
  };
}

function getLayoutCutStats(result, sheetW, sheetH) {
  const pieceSizeByElement = new Map();
  result.placedRects.forEach((rect) => {
    pieceSizeByElement.set(rectKey(rect), {
      w: rect.width ?? rect.pw,
      h: rect.height ?? rect.ph
    });
  });

  const cuts = buildCutsWithLabels(result);
  const mergedCuts = mergeCutSequences(cuts, pieceSizeByElement);
  const mergedSectorPairs = mergedCuts.filter(c => c.label === "Y" && c.count === 2).length;
  const savedCutRows = cuts.length - mergedCuts.length;
  return {
    cutLength: computeCutLength(mergedCuts, sheetW, sheetH),
    savedCutRows,
    mergedSectorPairs,
    mergeBonus: 0.35 * savedCutRows / Math.max(1, cuts.length) +
      0.1 * mergedSectorPairs / Math.max(1, cuts.filter(c => c.label === "Y").length)
  };
}

function scoreLayoutCandidate(result, totalRectCount, sheetW, sheetH) {
  const usedArea = result.placedRects.reduce(
    (sum, rect) =>
      sum +
      Math.max(0, (rect.width ?? 0) - KERF) *
        Math.max(0, (rect.height ?? 0) - KERF),
    0
  );
  const sheetArea = Math.max(SCORE_EPS, sheetW * sheetH);
  const utilization = usedArea / sheetArea;
  const placedRatio =
    totalRectCount > 0 ? result.placedRects.length / totalRectCount : 0;
  const cutStats = getLayoutCutStats(result, sheetW, sheetH);
  const { cutLength, mergeBonus } = cutStats;
  const cutLengthReference = Math.max(
    SCORE_EPS,
    (sheetW + sheetH) * Math.max(1, totalRectCount)
  );
  const normalizedCutLength = cutLength / cutLengthReference;
  const score = result._cutLayoutComplete
    ? 4 * utilization + 2 * placedRatio - 0.25 * normalizedCutLength + mergeBonus
    : -Infinity;

  return {
    ...cutStats,
    score,
    usedArea,
    utilization,
    placedCount: result.placedRects.length,
    placedRatio,
    remainingCount: result.remainingRects.length,
    cutLength,
    normalizedCutLength
  };
}

function isBetterLayoutCandidate(candidate, best) {
  if (!best) return true;
  const current = candidate._selectionStats;
  const previous = best._selectionStats;

  // Never add another sheet for nicer cuts when a valid candidate finishes
  // the remaining pieces on this sheet. Reject incomplete cut replays first.
  if (candidate._cutLayoutComplete !== best._cutLayoutComplete) {
    return candidate._cutLayoutComplete === true;
  }
  if ((current.remainingCount === 0) !== (previous.remainingCount === 0)) {
    return current.remainingCount === 0;
  }

  if (current.score > previous.score + SCORE_EPS) return true;
  if (previous.score > current.score + SCORE_EPS) return false;
  if (current.usedArea > previous.usedArea + SCORE_EPS) return true;
  if (previous.usedArea > current.usedArea + SCORE_EPS) return false;
  if (current.placedCount > previous.placedCount) return true;
  if (previous.placedCount > current.placedCount) return false;
  return current.cutLength < previous.cutLength - SCORE_EPS;
}

function runRootDirectionCandidates({
  rootDir,
  rects,
  sheetW,
  sheetH,
  tableIndex,
  id,
  TRIM,
  candidateCount,
  placementStrategy
}) {
  const layouts = generateLayouts({
    rootDir,
    rects,
    sheetW,
    sheetH,
    tableIndex,
    id,
    TRIM,
    config: { attempts: candidateCount, placementStrategy }
  });

  return layouts.map((layout) => {
    const result = addCutsToLayout({
      layout,
      rootDir,
      sheetW,
      sheetH,
      tableIndex,
      id,
      TRIM
    });

    return {
      ...result,
      _selectionStats: scoreLayoutCandidate(
        result,
        rects.length,
        sheetW,
        sheetH
      )
    };
  });
}

export function tryBothRootDirections({
  rects,
  sheetW,
  sheetH,
  tableIndex,
  id,
  mode,
  TRIM,
  direction = "both",
  placementStrategy = "grouped"
}) {
  if (mode === "replay") {
    const rootDir = rects[0]?.rootMainDir ?? CutDirection.HORIZONTAL;

    const rootSector = createSector({
      tableIndex,
      x: TRIM,
      y: TRIM,
      w: sheetW - TRIM,
      h: sheetH - TRIM,
      dir: rootDir,
      level: 0,
      rootMainDir: rootDir,
      isFullHeight: true
    });

    const remainingRects = [...rects];
    const placedRects = [];
    const cuts = [];

    processSectorFillOnTheFly(
      rootSector,
      remainingRects,
      placedRects,
      tableIndex,
      id,
      cuts,
      "replay",
      TRIM
    );

    return {
      placedRects,
      remainingRects,
      rootMainDir: rootDir,
      cuts
    };
  }

  const normalizedDirection = normalizeDirection(direction);
  const rootRuns =
    normalizedDirection === "both"
      ? [
          {
            rootDir: CutDirection.HORIZONTAL,
            candidateCount: Math.ceil(LAYOUT_CANDIDATE_COUNT / 2)
          },
          {
            rootDir: CutDirection.VERTICAL,
            candidateCount: Math.floor(LAYOUT_CANDIDATE_COUNT / 2)
          }
        ]
      : [
          {
            rootDir: normalizedDirection,
            candidateCount: LAYOUT_CANDIDATE_COUNT
          }
        ];

  const candidates = rootRuns.flatMap(({ rootDir, candidateCount }) =>
    runRootDirectionCandidates({
      rootDir,
      rects,
      sheetW,
      sheetH,
      tableIndex,
      id,
      TRIM,
      candidateCount,
      placementStrategy
    })
  );

  const best = candidates.reduce(
    (currentBest, candidate) =>
      isBetterLayoutCandidate(candidate, currentBest) ? candidate : currentBest,
    null
  );

  if (!best) {
    return {
      placedRects: [],
      remainingRects: [...rects],
      rootMainDir:
        normalizedDirection === "both"
          ? CutDirection.HORIZONTAL
          : normalizedDirection,
      cuts: []
    };
  }

  return {
    ...best,
    _selectionStats: {
      ...best._selectionStats,
      candidatesEvaluated: candidates.length
    }
  };
}

function getCutBaseLabel(c, rootMainDir) {
  return rootMainDir === CutDirection.HORIZONTAL
    ? c.vertical
      ? "Y"
      : "X"
    : c.vertical
      ? "X"
      : "Y";
}

function spanLength(c) {
  if (c.spanStart == null || c.spanEnd == null) return null;
  return Math.abs(c.spanEnd - c.spanStart);
}

function buildCutsWithLabels(result) {
  const verticalFullSpan = Math.max(
    ...result.cuts
      .filter((c) => c.vertical && spanLength(c) != null)
      .map((c) => spanLength(c)),
    0
  );

  const horizontalFullSpan = Math.max(
    ...result.cuts
      .filter((c) => !c.vertical && spanLength(c) != null)
      .map((c) => spanLength(c)),
    0
  );

  return result.cuts.map((c) => {
    const base = getCutBaseLabel(c, result.rootMainDir);
    const currentSpan = spanLength(c);
    const fullSpan = c.vertical ? verticalFullSpan : horizontalFullSpan;

    const isPartialSpan =
      !c.isTrim &&
      currentSpan != null &&
      fullSpan > 0 &&
      currentSpan < fullSpan - SAME_MM_TOL;

    const shouldBeInnerY = base === "Y" && isPartialSpan;

    return {
      ...c,
      label: resolveCutLabel({
        vertical: c.vertical,
        isTrim: c.isTrim,
        isSecondTrim: c.isSecondTrim,
        rootMainDir: result.rootMainDir,
        isInnerBandCut: c.isInnerBandCut || shouldBeInnerY,
        isInnerBandCutX: c.isInnerBandCutX
      })
    };
  });
}

function buildGeneratedTable({
  id,
  tableIndex,
  result,
  sheetW,
  sheetH,
  mode,
  existingTable,
  colorId,
  millingId,
  color,
  work,
  createdItems,
  isSplit = false,
  TRIM
}) {
  const cutsWithLabels = buildCutsWithLabels(result);
  const pieceSizeByElement = new Map();

  result.placedRects.forEach((r) => {
    pieceSizeByElement.set(rectKey(r), {
      w: r.width ?? r.pw,
      h: r.height ?? r.ph
    });
  });
  const mergedCuts = mergeCutSequences(cutsWithLabels, pieceSizeByElement);

  const riferimenti = buildRiferimenti({
    mergedCuts,
    placedRects: result.placedRects
  });

  const righe = buildRighe(mergedCuts);

  const righeAbs = buildRigheAbs(mergedCuts, cutsWithLabels);

  const dati = buildDati({
    placedRects: result.placedRects,
    createdItems,
    color,
    work,
    sheetWidth: sheetW,
    sheetHeight: sheetH,
    isRotated: result.rootMainDir === CutDirection.VERTICAL,
    isSplit,
    trim: TRIM,
    subtractKerf: mode !== "replay"
  });

  const areaUsed = getResultArea(result);
  const sheetArea = sheetW * sheetH;
  const kantLength = computeKantTotals(result.placedRects, createdItems);
  const cutLength = computeCutLength(mergedCuts, sheetW, sheetH);
  const basePrice = Number(color?.price ?? 0);
  const tablePrice = isSplit ? basePrice / 2 : basePrice;

  const table = {
    id,
    tableIndex,
    work: { workId: work.workId },
    color: { colorId: Number(colorId) },
    milling: millingId != null ? { millingId } : null,
    size:
      mode === "replay" && existingTable
        ? existingTable.size
        : `[${sheetH.toFixed(0)},${sheetW.toFixed(0)},18]`,
    tableUsage: sheetArea > 0 ? areaUsed / sheetArea : 0,
    partCount: result.placedRects.length,
    cutLength,
    measureCount: result.placedRects.length,
    kantLength,
    cutOrientation: result.rootMainDir === CutDirection.VERTICAL,
    righe,
    righeAbs,
    dati,
    riferimenti,
    frontend: true,
    price: tablePrice,
    trim: TRIM,
    split: isSplit === true
  };

  const fullExport = "\n" + dati + "\n" + righe + "\n" + riferimenti;
  console.group(
    `📄 EXPORT BLOCK id=${id} subMode=${isSplit ? "split" : "full"}`
  );
  console.log(fullExport);
  console.groupEnd();

  return table;
}

function buildReplayPlanForColor({
  rects,
  existingTablesForColor,
  colorId,
  millingId,
  color,
  work,
  createdItems,
  nextTempId,
  TRIM
}) {
  const placedRects = [];
  const createdTables = [];
  let remainingRects = [...rects];
  let tableIndex = 0;

  while (remainingRects.length > 0) {
    const existingTable = existingTablesForColor[tableIndex];

    if (!existingTable) {
      console.warn("Replay mode but no existing table found");
      break;
    }

    const id = existingTable.id ?? nextTempId--;

    const dims = existingTable.size
      .replace("[", "")
      .replace("]", "")
      .split(",")
      .map(Number);

    const replaySheetH = dims[0];
    const replaySheetW = dims[1];

    const rootMainDir = existingTable.cutOrientation
      ? CutDirection.VERTICAL
      : CutDirection.HORIZONTAL;

    const rootSector = createSector({
      tableIndex,
      x: TRIM,
      y: TRIM,
      w: replaySheetW - TRIM,
      h: replaySheetH - TRIM,
      dir: rootMainDir,
      level: 0,
      rootMainDir
    });

    const replayRemaining = [...remainingRects];
    const placed = [];
    const cuts = [];

    processSectorFillOnTheFly(
      rootSector,
      replayRemaining,
      placed,
      tableIndex,
      id,
      cuts,
      "replay",
      TRIM
    );

    const result = {
      placedRects: placed,
      remainingRects: replayRemaining,
      rootMainDir,
      cuts
    };

    if (result.placedRects.length === 0) {
      console.warn(`No rect could be placed for color ${colorId}, aborting`);
      break;
    }

    placedRects.push(...result.placedRects);

    createdTables.push(
      buildGeneratedTable({
        id,
        tableIndex,
        result,
        sheetW: replaySheetW,
        sheetH: replaySheetH,
        mode: "replay",
        existingTable,
        colorId,
        millingId,
        color,
        work,
        createdItems,
        isSplit: existingTable.split === true,
        TRIM
      })
    );

    remainingRects = result.remainingRects;
    tableIndex++;
  }

  return { placedRects, remainingRects, createdTables, nextTempId };
}

function buildFullTablesPlan({
  rects,
  sheetW,
  sheetH,
  colorId,
  millingId,
  color,
  work,
  createdItems,
  existingTablesForColor,
  nextTempId,
  TRIM,
  startTableIndex = 0,
  direction,
  placementStrategy
}) {
  const placedRects = [];
  const createdTables = [];
  let remainingRects = [...rects];
  let tableIndex = startTableIndex;

  while (remainingRects.length > 0) {
    let id = existingTablesForColor[tableIndex]?.id ?? nextTempId--;

    while (
      createdTables.some((t) => t.id === id) ||
      placedRects.some((r) => r.id === id)
    ) {
      id = nextTempId--;
    }
    const result = tryBothRootDirections({
      rects: remainingRects,
      sheetW,
      sheetH,
      tableIndex,
      id,
      mode: "optimize",
      TRIM,
      direction,
      placementStrategy
    });

    if (result.placedRects.length === 0) {
      console.warn(`No rect could be placed for color ${colorId}, aborting`);
      break;
    }

    placedRects.push(...result.placedRects);

    createdTables.push(
      buildGeneratedTable({
        id,
        tableIndex,
        result,
        sheetW,
        sheetH,
        mode: "optimize",
        existingTable: null,
        colorId,
        millingId,
        color,
        work,
        createdItems,
        isSplit: false,
        TRIM
      })
    );

    remainingRects = result.remainingRects;
    tableIndex++;
  }

  return {
    placedRects,
    remainingRects,
    createdTables,
    nextTempId,
    tableIndex
  };
}

function buildNoSplitPlan(args) {
  return {
    subMode: OptimizeSubMode.NOSPLIT,
    ...buildFullTablesPlan(args)
  };
}

function applyPostSplit(plan, args) {
  const {
    split,
    colorId,
    millingId,
    color,
    work,
    createdItems,
    TRIM,
    direction,
    placementStrategy
  } = args;

  if (!split || plan.placedRects.length === 0) return plan;

  let worstTable = null;
  let worstUtil = Infinity;

  for (const table of plan.createdTables) {
    if (!sameColorAndMilling(table, colorId, millingId)) continue;

    const util = tableUtilization(table, plan.placedRects);

    if (util < worstUtil) {
      worstUtil = util;
      worstTable = table;
    }
  }

  if (!worstTable) return plan;

  const rectsOnWorst = plan.placedRects
    .filter((r) => r.id === worstTable.id)
    .map(restoreOriginalRectForRetry);

  if (rectsOnWorst.length === 0) return plan;

  const retry = tryBothRootDirections({
    rects: rectsOnWorst,
    sheetW: split.w,
    sheetH: split.h,
    tableIndex: worstTable.tableIndex ?? 0,
    id: worstTable.id,
    mode: "optimize",
    TRIM,
    direction,
    placementStrategy
  });

  if (retry.remainingRects.length > 0) {
    console.warn(
      "❌ Postsplit retry FAILED — nem fért el a split táblán, marad a nosplit"
    );
    return plan;
  }

  const nextPlacedRects = plan.placedRects.filter(
    (r) => r.id !== worstTable.id
  );

  const nextCreatedTables = plan.createdTables.filter(
    (t) => t.id !== worstTable.id
  );

  nextPlacedRects.push(...retry.placedRects);

  nextCreatedTables.push(
    buildGeneratedTable({
      id: worstTable.id,
      tableIndex: worstTable.tableIndex ?? 0,
      result: retry,
      sheetW: split.w,
      sheetH: split.h,
      mode: "optimize",
      existingTable: null,
      colorId,
      millingId,
      color,
      work,
      createdItems,
      isSplit: true,
      TRIM
    })
  );

  return {
    ...plan,
    subMode: OptimizeSubMode.POSTSPLIT,
    placedRects: nextPlacedRects,
    createdTables: nextCreatedTables
  };
}

function buildPreSplitPlan(args) {
  const {
    rects,
    split,
    sheetW,
    sheetH,
    colorId,
    millingId,
    color,
    work,
    createdItems,
    existingTablesForColor,
    nextTempId,
    TRIM,
    direction,
    placementStrategy
  } = args;

  if (!split) return null;

  let localNextTempId = nextTempId;

  const splitId = existingTablesForColor[0]?.id ?? localNextTempId--;

  // ha a split meglévő/temp id-t kapott, biztosítsuk,
  // hogy a következő generált temp id már kisebb legyen
  if (typeof splitId === "number" && splitId < 0) {
    localNextTempId = Math.min(localNextTempId, splitId - 1);
  }
  const splitResult = tryBothRootDirections({
    rects,
    sheetW: split.w,
    sheetH: split.h,
    tableIndex: 0,
    id: splitId,
    mode: "optimize",
    TRIM,
    direction,
    placementStrategy
  });

  const placedRects = [];
  const createdTables = [];

  if (splitResult.placedRects.length > 0) {
    placedRects.push(...splitResult.placedRects);

    createdTables.push(
      buildGeneratedTable({
        id: splitId,
        tableIndex: 0,
        result: splitResult,
        sheetW: split.w,
        sheetH: split.h,
        mode: "optimize",
        existingTable: null,
        colorId,
        millingId,
        color,
        work,
        createdItems,
        isSplit: true,
        TRIM
      })
    );
  }

  const fullPlan = buildFullTablesPlan({
    rects: splitResult.remainingRects,
    sheetW,
    sheetH,
    colorId,
    millingId,
    color,
    work,
    createdItems,
    existingTablesForColor,
    nextTempId: localNextTempId,
    startTableIndex: 1,
    TRIM,
    direction,
    placementStrategy
  });

  return {
    subMode: OptimizeSubMode.PRESPLIT,
    placedRects: [...placedRects, ...fullPlan.placedRects],
    remainingRects: fullPlan.remainingRects,
    createdTables: [...createdTables, ...fullPlan.createdTables],
    nextTempId: fullPlan.nextTempId,
    tableIndex: fullPlan.tableIndex
  };
}

function chooseBestOptimizePlan(args) {
  const plans = [];
  // Compare complete jobs, not just the immediate benefit on the first sheet.
  // Keep pairing as one alternative, alongside large-area and wide-part plans.
  for (const placementStrategy of ["grouped", "area", "wide"]) {
    const strategyArgs = { ...args, placementStrategy };
    const noSplitPlan = buildNoSplitPlan(strategyArgs);
    plans.push(noSplitPlan);
    if (args.allowHalfTable !== false && args.split) {
      plans.push(applyPostSplit(noSplitPlan, strategyArgs));
      const preSplitPlan = buildPreSplitPlan(strategyArgs);
      if (preSplitPlan) plans.push(preSplitPlan);
    }
  }

  plans.forEach((plan) =>
    logOptimizePlanResult(plan, args.colors, args.work, args.createdItems)
  );

  return plans.reduce(
    (best, plan) =>
      isBetterPlan(plan, best, args.colors, args.work) ? plan : best,
    null
  );
}

export function processGenerate(context) {
  const {
    rectsByColor,
    work,
    getSheetSize,
    createdItems,
    mode = "optimize",
    TRIM: rawTrim,
    direction,
    allowHalfTable
  } = context;
  const parsedTrim = Number(rawTrim);
  const TRIM =
    Number.isFinite(parsedTrim) && parsedTrim >= 0 ? parsedTrim : 10;

  const resultByColor = {};
  const allPlacedRects = [];
  const createdTables = [];
  const existingTables = context.existingTables ?? [];
  let nextTempId = -1;

  for (const groupKey of Object.keys(rectsByColor)) {
    const [colorIdRaw, millingIdRaw] = groupKey.split("__");

    const colorId = Number(colorIdRaw);
    const millingId =
      millingIdRaw === "no-milling" ? null : Number(millingIdRaw);

    const existingTablesForColor = existingTables.filter((t) =>
      sameColorAndMilling(t, colorId, millingId)
    );

    const color = context.colors?.find(
      (c) => String(c.colorId) === String(colorId)
    );

    const isMDF =
      String(color?.materialType ?? "")
        .trim()
        .toUpperCase() === "MDF";

    let sheetW, sheetH;

    if (mode === "replay") {
      const firstTable = existingTablesForColor[0];

      if (!firstTable) {
        console.warn("Replay but no existing table");
        continue;
      }

      const dims = firstTable.size
        .replace("[", "")
        .replace("]", "")
        .split(",")
        .map(Number);

      sheetH = dims[0];
      sheetW = dims[1];
    } else {
      const size = getSheetSize(colorId);
      sheetW = size.sheetW - 2 * TRIM;
      sheetH = size.sheetH - 2 * TRIM;
    }

    if (isMDF) {
      const rects = rectsByColor[groupKey];

      if (!rects || rects.length === 0) {
        resultByColor[groupKey] = {
          placedRects: [],
          unplacedRects: []
        };
        continue;
      }

      const totalRectArea = rects.reduce((sum, r) => {
        const w = Number(r.width ?? r.pw);
        const h = Number(r.height ?? r.ph);

        if (!Number.isFinite(w) || !Number.isFinite(h)) return sum;

        return sum + w * h;
      }, 0);

      const tableUsage = Number((totalRectArea / 1_000_000).toFixed(3));
      const id = nextTempId--;

      const placed = rects.map((r) => ({
        ...r,
        id,
        mainid: r.mainid ?? r.createdItem?.itemId,
        xpos: r.xpos ?? 0,
        ypos: r.ypos ?? 0,
        rotated: r.rotated ?? false
      }));

      const table = {
        id,
        work: { workId: work.workId },
        color: { colorId: Number(colorId) },
        milling: millingId != null ? { millingId } : null,
        size: `[${sheetH.toFixed(0)},${sheetW.toFixed(0)},18]`,
        tableUsage,
        partCount: rects.length,
        cutLength: 0,
        measureCount: rects.length,
        kantLength: "-:0.0",
        cutOrientation: false,
        righe: "",
        righeAbs: "",
        dati: "",
        riferimenti: "",
        frontend: true,
        price: Number(color?.price ?? 0),
        trim: TRIM,
        split: false
      };

      createdTables.push(table);
      allPlacedRects.push(...placed);

      resultByColor[groupKey] = {
        placedRects: placed,
        unplacedRects: []
      };

      continue;
    }

    if (!sheetW || !sheetH) {
      console.warn(
        `❌ Invalid sheet size for color ${colorId}`,
        sheetW,
        sheetH
      );
      continue;
    }

    const rects = rectsByColor[groupKey] ?? [];

    let plan;

    if (mode === "replay") {
      plan = buildReplayPlanForColor({
        rects,
        existingTablesForColor,
        colorId,
        millingId,
        color,
        work,
        createdItems,
        nextTempId,
        TRIM
      });
    } else {
      const split = parseSplitDimension(color);

      plan = chooseBestOptimizePlan({
        rects,
        sheetW,
        sheetH,
        split,
        colorId,
        millingId,
        color,
        colors: context.colors,
        work,
        createdItems,
        existingTablesForColor,
        nextTempId,
        TRIM,
        direction,
        allowHalfTable
      });

      console.info(
        `✅ Optimize selected subMode=${plan?.subMode ?? "unknown"} color=${colorId}`
      );
    }

    if (!plan) {
      resultByColor[groupKey] = {
        placedRects: [],
        unplacedRects: rects
      };
      continue;
    }

    nextTempId = plan.nextTempId;

    createdTables.push(...plan.createdTables);
    allPlacedRects.push(...plan.placedRects);

    resultByColor[groupKey] = {
      placedRects: plan.placedRects,
      unplacedRects: plan.remainingRects
    };
  }

  if (mode === "replay") {
    const occupiedTableIds = new Set(Object.values(rectsByColor).flat()
      .map(rect => String(rect.id)));
    for (const existingTable of existingTables) {
      if (occupiedTableIds.has(String(existingTable.id))) continue;
      const [sheetH, sheetW] = JSON.parse(existingTable.size);
      const colorId = existingTable.color?.colorId;
      const color = context.colors?.find(c => String(c.colorId) === String(colorId));
      createdTables.push(buildGeneratedTable({
        id: existingTable.id, tableIndex: existingTable.tableIndex,
        result: {
          cuts: [], placedRects: [],
          rootMainDir: existingTable.cutOrientation
            ? CutDirection.VERTICAL : CutDirection.HORIZONTAL
        },
        sheetW, sheetH, mode, existingTable, colorId,
        millingId: existingTable.milling?.millingId ?? null,
        color, work, createdItems, isSplit: existingTable.split === true, TRIM
      }));
    }
  }

  return {
    byColor: resultByColor,
    placedRects: allPlacedRects,
    createdTables
  };
}
